import { logger } from '@adornis/base/logging.js';
import type { RouteOptimizationClient } from '@googlemaps/routeoptimization';
import type { google } from '@googlemaps/routeoptimization/build/protos/protos.js';
import { endOfDay, set, startOfDay } from 'date-fns';
import type { RouteInfo } from '../entities/RouteInfo.js';
import { buildShipments } from '../server/google-cloud/core/build-shipments.js';
import { buildVehicles } from '../server/google-cloud/core/build-vehicles.js';
import { toProtoTimestamp } from '../server/google-cloud/core/to-proto-timestamp.js';
import { transformResult } from '../server/google-cloud/core/transform-result.js';
import { googleCloudSetup } from '../server/google-cloud/setup.js';
import {
  TICKET_COST_CLIENT,
  TICKET_COST_ENT,
  TICKET_COST_HES,
  TICKET_COST_IMAC,
  VEHICLE_COST_PER_DAY,
} from '../shared/pricing.js';
import type { IRouteDepot, IRouteEmployee, IRouteTicket } from '../types.js';

// Verladedauer am Depot pro Ticket (in Minuten)
export const DEPOT_SERVICE_DURATION_MINUTES = 0;

const EMPLOYEE_MAX_WORKING_HOURS = 7;
export const VEHICLE_COST_PER_KILOMETER = 0.4;
export const GLOBAL_DURATION_COST_PER_HOUR = 0;
// export const GLOBAL_VEHICLE_COST_PER_DAY = (VEHICLE_COST_PER_DAY + FE_COST_PER_DAY) * 10;
export const GLOBAL_VEHICLE_COST_PER_DAY = VEHICLE_COST_PER_DAY;
export const TICKET_PENALTY_COST_CLIENT = TICKET_COST_CLIENT * 20;
export const TICKET_PENALTY_COST_ENT = TICKET_COST_ENT * 20;
export const TICKET_PENALTY_COST_HES = TICKET_COST_HES * 20;
export const TICKET_PENALTY_COST_IMAC = TICKET_COST_IMAC * 20;
// export const PENALTY_FOR_MISSING_ALREADY_ASSIGNED_TICKET = 12900;
export const HARD_LIMIT_TRAVEL_DURATION_IN_SECONDS = 5 * 60 * 60; // 5 hours
export const LONG_TRANSITION_PENALTY_THRESHOLD_METERS = 50_000;
export const LONG_TRANSITION_PENALTY_COST_PER_KILOMETER = 5;

// + 60 = Eine Stunde puffer, damit jeder Tag ausreichend gefüllt wird
// - 30 = Pauschal 30min Depotzeit (für Beladung etc.)
export const VEHICLE_MAX_ROUTE_DURATION_MINUTES = EMPLOYEE_MAX_WORKING_HOURS * 60 + 60 - 30; // reduced working time of 7:30h

// export const VEHICLE_DURATION_COST_PER_HOUR = FE_COST_PER_DAY / EMPLOYEE_MAX_WORKING_HOURS; // ~34.30€/h
export const VEHICLE_DURATION_COST_PER_HOUR = 0;

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 30_000;
const MAX_DELAY_MS = 30_000;

const calculateTimeoutSeconds = (tickets: IRouteTicket[], employees: IRouteEmployee[]): number => {
  const baseSeconds = 180;
  const perTicketSeconds = 5;
  const perEmployeeSeconds = 5;
  const maxSeconds = 900;

  return Math.min(maxSeconds, baseSeconds + tickets.length * perTicketSeconds + employees.length * perEmployeeSeconds);
};

const isRateLimitError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const err = error as {
    code?: number;
    message?: string;
    reason?: string;
    errors?: Array<{ reason?: string; message?: string }>;
    response?: {
      status?: number;
      statusText?: string;
      data?: {
        code?: number;
        reason?: string;
        message?: string;
        errors?: Array<{ reason?: string; message?: string }>;
      };
    };
  };

  const codes = [err.code, err.response?.status, err.response?.data?.code].filter(
    (code): code is number => typeof code === 'number',
  );

  const reasons = [
    err.reason,
    ...(err.errors?.map(e => e.reason) ?? []),
    err.response?.data?.reason,
    ...(err.response?.data?.errors?.map(e => e.reason) ?? []),
  ].filter((reason): reason is string => typeof reason === 'string');

  const messages = [
    err.message,
    err.response?.statusText,
    err.response?.data?.message,
    ...(err.errors?.map(e => e.message) ?? []),
    ...(err.response?.data?.errors?.map(e => e.message) ?? []),
  ].filter((message): message is string => typeof message === 'string');

  return (
    codes.some(code => code === 429) ||
    reasons.some(reason => reason === 'rateLimitExceeded') ||
    messages.some(message => message.includes('429') || message.includes('rateLimitExceeded'))
  );
};

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export const callOptimizeToursWithRetry = async (
  client: RouteOptimizationClient,
  args: google.maps.routeoptimization.v1.IOptimizeToursRequest,
  maxRetries = MAX_RETRIES,
): Promise<[google.maps.routeoptimization.v1.IOptimizeToursResponse, ...unknown[]]> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      logger.info({
        timeout: args.timeout,
        numShipments: args.model?.shipments?.length,
        numVehicles: args.model?.vehicles?.length,
      });
      const result = await client.optimizeTours(args);
      await sleep(3_000);
      return result;
    } catch (error) {
      if (attempt === maxRetries || !isRateLimitError(error)) {
        throw error;
      }

      const delayMs = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt) + Math.floor(Math.random() * 1000);
      logger.warn(
        { attempt: attempt + 1, maxRetries, delayMs, error },
        'Route optimization rate limited, retrying with exponential backoff.',
      );
      await sleep(delayMs);
    }
  }

  throw new Error('Route optimization retries exhausted unexpectedly');
};

export type routePlanningOptionsType = {
  modelOptions?: Omit<google.maps.routeoptimization.v1.IOptimizeToursRequest['model'], 'shipments' | 'vehicles'>;
  vehicleOptions?: (
    engineer: IRouteEmployee,
  ) => Partial<Omit<google.maps.routeoptimization.v1.IVehicle, 'displayName' | 'label' | 'travelMode'>>;
  // Overrides the geocoded depot pickup location per depot id. Used e.g. for partner
  // routes where the PUDO coordinates are known and should drive the optimization
  // instead of the real depot address.
  depotLocationOverrides?: Map<string, { latitude: number; longitude: number }>;
};

export const optimizeRoute = async (
  employees: IRouteEmployee[],
  depots: IRouteDepot[],
  tickets: IRouteTicket[],
  // assignedTickets: IRouteTicketAssigned[],
  dayToPlan: Date,
  routePlanningOptions?: routePlanningOptionsType,
): Promise<{
  routes: RouteInfo[];
  input: google.maps.routeoptimization.v1.IOptimizeToursRequest;
  output: google.maps.routeoptimization.v1.IOptimizeToursResponse;
}> => {
  const { routeOptimizationClient, projectId } = googleCloudSetup();

  if (!projectId) {
    throw new Error('Missing GOOGLE_CLOUD_PROJECT_NAME for route optimization.');
  }

  // Kombiniere alle tickets (assigned + unassigned)
  const allTickets: IRouteTicket[] = [...tickets];

  const globalTimeWindow = {
    start: set(startOfDay(dayToPlan), { seconds: 0, milliseconds: 0 }),
    end: set(endOfDay(dayToPlan), { seconds: 0, milliseconds: 0 }),
  };

  const shipments = await buildShipments({
    tickets: allTickets,
    depots,
    employees,
    depotLocationOverrides: routePlanningOptions?.depotLocationOverrides,
    globalTimeWindow,
  });
  const vehicles = await buildVehicles({
    tickets: allTickets,
    depots,
    employees,
    vehicleOptions: routePlanningOptions?.vehicleOptions,
  });

  // Group shipments by their base type (e.g., "location-123") to identify anchor-member relationships.
  // Anchors are the "primary" shipments at a location, members are additional shipments that must
  // be performed by the same vehicle. This creates dependencies so that if a vehicle visits a location,
  // it handles all related shipments there together.
  const groupedShipmentTypes = shipments.reduce(
    (acc, shipment) => {
      if (!shipment.shipmentType) return acc;
      const anchorSuffix = '-anchor';
      const memberSuffix = '-member';

      if (shipment.shipmentType.endsWith(anchorSuffix)) {
        const baseType = shipment.shipmentType.slice(0, -anchorSuffix.length);
        const currentGroup = acc[baseType] ?? { anchorTypes: new Set<string>(), memberTypes: new Set<string>() };
        currentGroup.anchorTypes.add(shipment.shipmentType);
        acc[baseType] = currentGroup;
      } else if (shipment.shipmentType.endsWith(memberSuffix)) {
        const baseType = shipment.shipmentType.slice(0, -memberSuffix.length);
        const currentGroup = acc[baseType] ?? { anchorTypes: new Set<string>(), memberTypes: new Set<string>() };
        currentGroup.memberTypes.add(shipment.shipmentType);
        acc[baseType] = currentGroup;
      }

      return acc;
    },
    {} as Record<string, { anchorTypes: Set<string>; memberTypes: Set<string> }>,
  );
  const shipmentTypeRequirements = Object.values(groupedShipmentTypes)
    .map(groupedType => {
      if (groupedType.anchorTypes.size === 0 || groupedType.memberTypes.size === 0) {
        return null;
      }

      const requiredShipmentTypeAlternatives = [...groupedType.anchorTypes];
      const dependentShipmentTypes = [...groupedType.memberTypes].filter(
        memberType => !groupedType.anchorTypes.has(memberType),
      );

      if (dependentShipmentTypes.length === 0) {
        return null;
      }

      return {
        requiredShipmentTypeAlternatives,
        dependentShipmentTypes,
        requirementMode: 'PERFORMED_BY_SAME_VEHICLE' as const,
      };
    })
    .filter((value): value is NonNullable<typeof value> => !!value);

  const constructedArgs: google.maps.routeoptimization.v1.IOptimizeToursRequest = {
    // Google Cloud Console - Projekt-ID
    parent: `projects/${projectId}`,

    // consider road traffic doesnt mean that optimization is based on current traffic, it just means that the estimated travel times after optimization are based on current traffic (we're using time windows for when the optimization is running so traffic from that time span is considered not from when the optmization runs)
    considerRoadTraffic: true,

    searchMode: 'CONSUME_ALL_AVAILABLE_TIME',
    timeout: { seconds: calculateTimeoutSeconds(allTickets, employees) },

    model: {
      shipments,
      vehicles,
      transitionAttributes: [
        {
          excludedSrcTag: 'unused-transition-attribute-tag',
          excludedDstTag: 'unused-transition-attribute-tag',
          distanceLimit: {
            softMaxMeters: LONG_TRANSITION_PENALTY_THRESHOLD_METERS,
            costPerKilometerBelowSoftMax: 0,
            costPerKilometerAboveSoftMax: LONG_TRANSITION_PENALTY_COST_PER_KILOMETER,
          },
        },
      ],
      ...(shipmentTypeRequirements.length > 0 ? { shipmentTypeRequirements } : {}),
      // Optimierungsziel: Möglichst wenig vehicles nutzen
      // Höhere Kosten pro Stunde führen dazu, dass Google versucht,
      // die Gesamtarbeitszeit zu minimieren und dabei alle Vehicles gleichmäßig auszulasten
      // -> Preis muss auf irgendeinen Wert gesetzt werden, damit Google optmieren kann.
      globalDurationCostPerHour: GLOBAL_DURATION_COST_PER_HOUR,
      globalStartTime: toProtoTimestamp(globalTimeWindow.start),
      globalEndTime: toProtoTimestamp(globalTimeWindow.end),

      ...(routePlanningOptions?.modelOptions ?? {}),
    },
  };

  try {
    const [response] = await callOptimizeToursWithRetry(routeOptimizationClient, constructedArgs);

    const routes = await transformResult(response, allTickets, depots, employees);
    return { routes, input: constructedArgs, output: response };
  } catch (err) {
    const error = err as {
      message?: string;
      response?: { status?: number; statusText?: string; data?: unknown };
    };
    const responseData = error.response?.data;
    const decodedResponseText = (() => {
      if (Array.isArray(responseData) && responseData.every(entry => typeof entry === 'number')) {
        return String.fromCharCode(...responseData);
      }

      if (responseData instanceof Uint8Array) {
        return new TextDecoder().decode(responseData);
      }

      if (responseData instanceof ArrayBuffer) {
        return new TextDecoder().decode(new Uint8Array(responseData));
      }

      if (typeof responseData === 'string') {
        const maybeNumbers = responseData.split(',').map(entry => Number(entry.trim()));
        if (maybeNumbers.length > 0 && maybeNumbers.every(value => Number.isFinite(value))) {
          return String.fromCharCode(...maybeNumbers);
        }
      }

      return undefined;
    })();
    const responseText =
      decodedResponseText ??
      (typeof responseData === 'string' ? responseData : responseData ? JSON.stringify(responseData) : undefined);

    logger.error(
      {
        errorMessage: error.message,
        errorStack: err instanceof Error ? err.stack : undefined,
        responseText,
        projectId,
        employees,
        tickets,
        routePlanningOptions,
      },
      'Route optimization failed while calling optimizeTours.',
    );

    throw err;
  }
};
